import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
// Package.json'dan versiyon bilgisini al
import packageInfo from '../../package.json';

const LoginPage = ({ onLoginSuccess }) => {
  // Kullanıcının son remember_me tercihini localStorage'dan oku
  const getSavedRememberMe = () => {
    try {
      const saved = localStorage.getItem('rememberMe');
      return saved === 'true';
    } catch (e) {
      return false;
    }
  };

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: getSavedRememberMe()
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Role seçimi (ADMIN | AGENCY | DRIVER)
  const resolveInitialRole = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qp = params.get('role');
      if (['ADMIN', 'AGENCY', 'DRIVER'].includes(qp)) return qp;
      const saved = localStorage.getItem('loginRole');
      if (['ADMIN', 'AGENCY', 'DRIVER'].includes(saved)) return saved;
    } catch (e) {}
    return 'ADMIN';
  };
  
  const [role, setRole] = useState(resolveInitialRole());
  
  useEffect(() => {
    try { 
      localStorage.setItem('loginRole', role); 
    } catch (e) {}
  }, [role]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Hata mesajını temizle
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const isPhone = (v) => {
    const d = (v||'').replace(/\D/g,'');
    if (d.startsWith('0') && d.length===11) return true;
    if (d.length===10) return true;
    if (d.startsWith('90') && d.length===12) return true;
    if (d.startsWith('+90') && d.length===13) return true;
    return false;
  };

  const normalizePhone = (v) => {
    if (role !== 'DRIVER') {
      // Admin/Agency için eski format
      const d = (v||'').replace(/\D/g,'');
      if (d.startsWith('0') && d.length===11) return '+90'+d.slice(1);
      if (d.length===10) return '+90'+d;
      if (d.startsWith('90') && d.length===12) return '+'+d;
      if (d.startsWith('+90') && d.length===13) return v;
      return v;
    } else {
      // Şoför için yeni format - orijinal formatı koru
      return v;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const isDriverSelected = role === 'DRIVER';
    
    if (!formData.email.trim()) {
      newErrors.email = isDriverSelected ? 'Telefon gereklidir' : 'Email gereklidir';
    } else if (isDriverSelected && !isPhone(formData.email)) {
      newErrors.email = 'Geçerli bir telefon giriniz';
    } else if (!isDriverSelected && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Geçerli bir email giriniz';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Şifre gereklidir';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const loginPayload = role === 'DRIVER'
        ? { phone: normalizePhone(formData.email), password: formData.password, remember_me: formData.rememberMe }
        : { email: formData.email, password: formData.password, remember_me: formData.rememberMe };

      const endpoint = role === 'DRIVER' ? '/api/driver/login' : '/api/auth/login';
      const response = await api.post(endpoint, loginPayload);

      let userData;
      
      if (role === 'DRIVER') {
        // Şoför login response formatı
        const { access_token, driver_id, driver_name, user_id } = response.data;
        
        if (!access_token || !driver_name || !user_id) {
          throw new Error('Geçersiz şoför login yanıtı');
        }
        
        userData = {
          id: user_id,
          name: driver_name,
          role: 'DRIVER',
          driver_id: driver_id,
          token: access_token
        };
        
        // LocalStorage'a kaydet
        localStorage.setItem('token', access_token);
        localStorage.setItem('role', 'DRIVER');
        localStorage.setItem('user', JSON.stringify({
          id: user_id,
          name: driver_name,
          role: 'DRIVER',
          driver_id: driver_id
        }));
        localStorage.setItem('driver_id', driver_id.toString());
        
      } else {
        // Admin/Agency login response formatı
        const { access_token, user_id, role: userRole, name, agency_id, agency_name } = response.data;
        
        if (!access_token || !userRole || !name) {
          throw new Error('Geçersiz login yanıtı');
        }
        
        userData = {
          id: user_id,
          name: name,
          role: userRole,
          agency_id: agency_id,
          agency_name: agency_name,
          token: access_token
        };
        
        // LocalStorage'a kaydet
        localStorage.setItem('token', access_token);
        localStorage.setItem('role', userRole);
        localStorage.setItem('user', JSON.stringify({ 
          id: user_id, 
          name: name,
          role: userRole,
          agency_id: agency_id,
          agency_name: agency_name
        }));
        if (agency_id) {
          localStorage.setItem('agency_id', agency_id.toString());
        }
      }

      // Login başarılı callback'i çağır
      if (onLoginSuccess) {
        onLoginSuccess(userData);
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setErrors({
        general: error.response?.data?.detail || error.response?.data?.message || 'Giriş yapılırken bir hata oluştu'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            HTS - Transfer Sistemi
          </h1>
          <p className="text-gray-600">Hastane Transfer Sistemi</p>
        </div>

        {/* Login Form */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8 hover:shadow-2xl transition-all duration-300">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rol Seçici */}
            <div className="flex justify-center">
              <div className="inline-flex rounded-xl bg-slate-100 p-1">
                {[
                  { key: 'ADMIN', label: 'Admin' },
                  { key: 'AGENCY', label: 'Acente&Birimler' },
                  { key: 'DRIVER', label: 'Şoför' },
                ].map(opt => (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => setRole(opt.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      role === opt.key ? 'bg-white shadow border border-slate-200' : 'text-slate-600 hover:text-slate-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Email/Phone Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {role === 'DRIVER' ? 'Telefon' : 'E-mail'}
              </label>
              <input
                id="email"
                name="email"
                type="text"
                autoComplete="username"
                required
                value={formData.email}
                onChange={handleInputChange}
                placeholder=""
                className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/50 backdrop-blur-sm transition-all duration-200 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2 leading-tight py-1">
                Şifre
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleInputChange}
                placeholder=""
                className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/50 backdrop-blur-sm transition-all duration-200 ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center">
              <input
                id="rememberMe"
                name="rememberMe"
                type="checkbox"
                checked={formData.rememberMe}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFormData(prev => ({ ...prev, rememberMe: checked }));
                  // Kullanıcının tercihini hatırla
                  try {
                    localStorage.setItem('rememberMe', checked.toString());
                  } catch (e) {}
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
              />
              <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                Beni hatırla
              </label>
            </div>

            {/* Forgot Password Link */}
            <div className="text-center">
              <a
                href="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors leading-tight py-1"
              >
                Şifrenizi mi unuttunuz?
              </a>
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="text-center">
                <p className="text-sm text-red-600">{errors.general}</p>
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-bht-red to-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 leading-tight"
            >
              {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center">
          <div className="inline-block">
            <div className="text-xl font-bold">
              <span className="text-bht-red">BHT</span>{' '}
              <span className="text-bht-gray">CLINIC</span>
            </div>
            <div className="text-sm text-bht-gray mt-1">
              HASTANE TRANSFER SİSTEMİ
            </div>
            {/* Versiyon Numarası */}
            <div className="mt-3 text-xs text-gray-400 bg-gray-50/50 backdrop-blur-sm px-3 py-1 rounded-full inline-block border border-gray-200/30">
              <span className="font-mono">v{packageInfo.version}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
